<template>
  <!-- Boards Public View -->
  <div class="w-full h-full relative bg-none">
    <!-- Whitelabel -->
    <transition name="fadeHeader" mode="out-in">
      <WhiteLabelHeader
        v-if="lensTeam !== null"
        hide-dupe-opt
        :board-team="lensTeam"
        :shared-by="sharedBy"
        :loading-platform="loading.platform"
        share-type="creative"
      />
      <WhiteLabelHeader
        v-else
        :board-team="lensTeam"
        hide-dupe-opt
        :shared-by="sharedBy"
        :loading-platform="loading.platform"
        share-type="creative"
        :show-default="true"
      />
    </transition>

    <MainLayout aditional-classes="ml-4 mb-4" no-content-padding no-bottom-content-padding
    style="height: calc(100vh - 88px); width: calc(100vw - 30px); z-index: 99; position: relative">
      <template #content>
        <!-- Loading State -->
        <div v-if="loading.share || loading.subscription" class="flex items-center justify-center w-full h-full mt-24">
          <BaseLoadingSpinnerCircle class="w-10 h-10 text-center text-icon-normal" />
        </div>
        <!-- Error State -->
        <div v-else-if="error?.status" class="h-full mt-32">
          <div class="flex flex-col gap-3 items-center justify-center w-full h-full">
            <BaseText type="heading" class="text-text-subdued text-center">
              {{ error.status || 500 }}
            </BaseText>
            <BaseText type="label" class="text-text-muted text-center">
              <span v-if="error.status === 404">
                Creative not found
              </span>
              <span v-else-if="error.status === 401 || error.status === 403">
                No access
              </span>
              <span v-else>
                Error
              </span>
            </BaseText>
            <BaseText size="sm" class="text-text-subdued text-center" style="max-width: 280px">
              <span v-if="error.status === 401">
                You dont have permission to view this share link
              </span>
              <span v-else-if="error.status === 403 || error.status === 404">
                This share link does not exist, or you do not have permission to view it
              </span>
              <span v-else>
                Uh oh something went wrong...
              </span>
            </BaseText>
            <router-link
              v-slot="{ navigate, href }"
              :to="{ name: 'LensView' }"
              class="mt-4 inline-block"
              custom
            >
              <button
                :href="href"
                class="transition-colors bg-transparent hover:bg-background-hover rounded-md px-2 py-1"
                type="button"
                @click="navigate"
              >
                <span>Back to Lens</span>
              </button>
            </router-link>
          </div>
        </div>
        <div
          v-else-if="adDetailsData !== null"
        >
          <!-- Mark is-public true for shared creatives (until we fix tagging) -->
          <AdInsightsDrawer
            no-drawer
            :is-public="true"
            :brand="brand"
            :ad-group-data="adDetailsData"
            :loading="loading.share"
            :loading-metrics="loading.adDetailsMetrics"
            :all-ads="adDetailsData?.ads"
            :date-range="selectedDateRange"
            :selected-columns="selectedColumns"
            @refetch-insights="fetchCreativeGroup"
          />
        </div>
      </template>
    </MainLayout>
  </div>
</template>

<script>
import LensAPI from '@/api/lens'
import MainLayout from '../../layouts/MainLayout'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import FirebaseAPI from '@/api/firebase'
import AdvertisementList from '../../components/advertisements/AdvertisementList'
import WhiteLabelHeader from '../../components/account/whiteLabel/WhiteLabelHeader.vue'
import AdInsightsDrawer from '../../components/lens/report/AdInsightsDrawer.vue'
import { parseDateRangeFromQuery } from '../../utils/lens/filters/filterQueryUtils'
import { reverseProviderKeyMap } from '@/utils/lens/dataSourceUtils'

export default {
  name: 'AdsShareLensView',
  components: {
    AdvertisementList,
    MainLayout,
    WhiteLabelHeader,
    AdInsightsDrawer
  },
  data () {
    return {
      loading: {
        share: true,
        adDetailsMetrics: false,
        subscription: true,
        platform: true
      },
      userSubscribed: true,
      lensTeam: null,
      sharedBy: null,
      // Data
      adDetailsData: null,
      brand: null,
      selectedDateRange: null,
      sharedCreative: null,
      selectedColumns: [],
      error: {
        status: null,
        message: null
      }
    }
  },
  computed: {
    ...mapGetters('BoardsModule', ['getBoards']),
    ...mapGetters('AuthModule', ['getUser', 'isUserLoggedIn']),
    ...mapGetters('MetricsModule', ['getMetricLookup'])

  },
  async mounted () {
    this.loading.share = true
    // Since there is no navbar, we need to initialize some Vuex state here
    await this.fetchBoards()
    await this.fetchFolders()
    await this.SET_STARRED_BOARDS(
      this.getBoards.filter((board) =>
        this.getUser.starredBoards?.includes(board.id)
      )
    )

    await this.fetchSharedCreative()
    await this.checkIfUserSubscribed()
    this.loading.share = false
    this.loading.subscription = false
  },
  methods: {
    ...mapActions('BoardsModule', ['fetchBoards', 'fetchFolders']),
    ...mapActions('MetricsModule', ['setMetricsPlatform', 'fetchLensMetrics', 'updateIsCustomPreset', 'setPresetById', 'updateLocalPresetField']),
    ...mapMutations('BoardsModule', ['SET_BOARDS']),
    ...mapMutations('AuthModule', ['SET_STARRED_BOARDS']),

    async checkIfUserSubscribed () {
      return true
    },
    // Go to Signup
    goToSignup () {
      this.SET_BOARDS([])
      this.$router.push({ name: 'LoginView' })
    },
    async fetchSharedCreative () {
      this.loading.share = true
      try {
        // Get the stored shared creative metadata
        const { data: shareRes } = await LensAPI.AdGroups.getShareCreative(this.$route.params.id)
        const shareData = shareRes.shared_creative
        if (!shareRes) {
          this.$showAlert({
            message: 'Could not find creative',
            type: 'error'
          })
          return
        }

        // Set the metrics platform based on the provider returned by the share creative
        this.setMetricsPlatform(reverseProviderKeyMap[shareData.provider])
        this.loading.platform = false

        if (shareData?.is_public && !this.isUserLoggedIn) {
          await this.fetchLensMetrics({ lensId: null, registryOverride: shareRes.metrics_registry })
        }

        // Set whitelabel data
        this.sharedBy = shareData?.created_by ? await FirebaseAPI.Users.getById(shareData?.created_by) : null
        this.lensTeam = shareData?.team_id ? await FirebaseAPI.Teams.getByID(shareData?.team_id) : null

        this.sharedCreative = shareRes?.shared_creative
        this.adDetailsData = {
          ...shareRes.creative_data,
          aiTransaction: shareRes.creative_data?.ai_transaction,
          isCreativeGroup: true,
          isDataPublic: this.sharedCreative.is_public
        }

        // Only keep the date filters
        const dateFilters = shareData.creative_query.query.filters.filter(filter => filter?.[0]?.key === 'event_date')
        this.selectedDateRange = parseDateRangeFromQuery(dateFilters)

        const baseColumns = this.sharedCreative.creative_query?.preset?.table_columns || this.sharedCreative.creative_query?.query?.selectedColumns

        this.selectedColumns = baseColumns.map(key => ({ key, is_pinned: false }))

        const enableCustom = this.sharedCreative.is_public || !this.sharedCreative.creative_query?.preset?.id
        this.updateIsCustomPreset(enableCustom)

        const lens = this.sharedCreative.lens
        this.brand = {
          ...lens,
          name: lens?.company_name ?? lens?.name ?? ''
        }
      } catch (e) {
        console.error(e)
        const errStatus = e?.response?.status
        this.error.status = errStatus
        if (errStatus === 403 || errStatus === 404) {
          const errMessage = e?.response?.data?.message || 'This share link does not exist, or you do not have permission to view it'
          this.error.message = errMessage
          this.$showAlert({
            message: errMessage,
            type: 'error'
          })
          return
        }
        this.$showAlert({
          message: e?.response?.data?.message || 'Error fetching creative',
          type: 'error'
        })
      } finally {
        this.loading.share = false
      }
    },
    async fetchCreativeGroup (additionalColumns = []) {
      this.loading.adDetailsMetrics = true
      if (additionalColumns.length) {
        // Additional columns are only populated when refetching from the insights drawer after a preset change
        this.loading.adDetailsMetrics = true
      }
      try {
        const { creativeHash, group_by: groupBy, query: creativeQuery, preset } = this.sharedCreative.creative_query

        const idPayload = {
          lensId: this.sharedCreative.lens_id,
          creativeHash,
          groupBy
        }
        // Only keep the date filters
        const dateFilters = creativeQuery.filters.filter(filter => filter?.[0]?.key === 'event_date')
        this.selectedDateRange = parseDateRangeFromQuery(dateFilters)

        const baseColumns = preset?.table_columns || creativeQuery.selectedColumns
        this.selectedColumns = [...new Set([
          ...baseColumns.map(key => ({ key, is_pinned: false })),
          ...additionalColumns.map(key => ({ key, is_pinned: false }))
        ])]

        const query = {
          group: groupBy,
          ...creativeQuery,
          filters: [...dateFilters],
          selectedColumns: this.selectedColumns
        }

        const adDetails = await LensAPI.AdGroups.getLensCreativeGroup(idPayload, query)

        this.adDetailsData = {
          ...adDetails.data,
          aiTransaction: adDetails.data.ai_transaction,
          isCreativeGroup: true,
          isDataPublic: this.sharedCreative.is_public
        }
      } catch (e) {
        console.error(e)
        this.$showAlert({
          message: e,
          type: 'error'
        })
      } finally {
        this.loading.adDetailsMetrics = false
      }
    }

  }
}
</script>
<style scoped>
.fadeHeader-enter-active,
.fadeHeader-leave-active {
  transition: all 0.2s ease-out;
  opacity: 1;
}

.fadeHeader-enter,
.fadeHeader-leave-to {
  opacity: 0;
}
</style>
